// "Photo Health" dashboard widget — PR staff only (director/administrator/consultant).
// Proves photo uploads from site forms are actually working: how many succeeded vs
// permanently failed vs needed a retry on a flaky connection, plus how many are stuck
// on a device right now (and exactly who/where). Toggle between This week / This month / Lifetime.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, CheckCircle2, AlertTriangle, RefreshCw, Smartphone, ArrowRight, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const RANGES = [
    { key: "week", label: "This week" },
    { key: "month", label: "This month" },
    { key: "lifetime", label: "Lifetime" },
];

function timeAgo(iso) {
    if (!iso) return "";
    const ms = Date.now() - new Date(iso).getTime();
    const mins = Math.round(ms / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 48) return `${hrs}h ago`;
    return `${Math.round(hrs / 24)}d ago`;
}

export default function PhotoHealthWidget({ brand = "var(--brand-accent, #C75A1B)" }) {
    const [range, setRange] = useState("week");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showStuck, setShowStuck] = useState(false);
    const [showFailed, setShowFailed] = useState(false);
    const [dismissing, setDismissing] = useState(null);
    const navigate = useNavigate();

    const load = () => {
        setLoading(true);
        api.get("/forms/photo-health/stats", { params: { range } })
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    };

    useEffect(load, [range]);

    const uploaded = data?.uploaded ?? 0;
    const failed = data?.failed ?? 0;
    const retried = data?.offline_retry ?? 0;
    const attempts = uploaded + failed;
    const successRate = attempts > 0 ? Math.round((uploaded / attempts) * 100) : null;
    const stuck = data?.currently_stuck || { count: 0, devices: 0, items: [] };
    const failedItems = data?.failed_items || [];

    const dismissFailed = async (eventId) => {
        setDismissing(eventId);
        try {
            await api.post(`/forms/photo-health/failed/${eventId}/dismiss`);
            toast.success("Dismissed");
            load();
        } catch {
            toast.error("Couldn't dismiss — try again");
        } finally {
            setDismissing(null);
        }
    };

    return (
        <div data-testid="photo-health-widget" className="bg-white border rounded-[11px] shadow-brand-sm overflow-hidden" style={{ borderColor: "#E5E0DA" }}>
            <div className="px-5 py-3.5 flex items-center justify-between border-b flex-wrap gap-2.5" style={{ borderColor: "#F0EDE8" }}>
                <div className="flex items-center gap-2">
                    <Camera className="w-4 h-4" style={{ color: brand }} />
                    <h3 className="text-[14px] font-semibold text-[#1F1F1F]">Photo health</h3>
                    <span className="text-[10.5px]" style={{ color: "#9A968F" }}>Site-form photo uploads</span>
                </div>
                <div className="inline-flex rounded-full border p-0.5" style={{ borderColor: "#E5E0DA" }} data-testid="photo-health-range-toggle">
                    {RANGES.map((r) => (
                        <button key={r.key} data-testid={`photo-health-range-${r.key}`}
                            onClick={() => setRange(r.key)}
                            className="px-3 py-1 rounded-full text-[11px] font-semibold transition-colors"
                            style={range === r.key ? { background: brand, color: "white" } : { color: "#6F6A64" }}>
                            {r.label}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="px-5 py-8 text-center text-[12.5px] italic" style={{ color: "#9A968F" }}>Loading…</div>
            ) : !data ? (
                <div className="px-5 py-8 text-center text-[12.5px] italic" style={{ color: "#9A968F" }}>Couldn't load photo health stats.</div>
            ) : (
                <div className="p-5">
                    <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))" }}>
                        <Stat testId="photo-health-uploaded" icon={CheckCircle2} iconColor="#3F7D3A" bg="#EEF6EC"
                              label="Uploaded" value={uploaded} />
                        <Stat testId="photo-health-retried" icon={RefreshCw} iconColor="#C68A1C" bg="#FCF1DC"
                              label="Needed a retry" value={retried} sub="offline / flaky signal, resolved automatically" />
                        <Stat testId="photo-health-failed" icon={AlertTriangle} iconColor="#B33A2E" bg="#FBE8E5"
                              label="Failed" value={failed} sub="permanent — needs manual retry/discard"
                              clickable={failed > 0} onClick={() => setShowFailed((s) => !s)} />
                        <Stat testId="photo-health-success-rate" icon={Camera} iconColor={brand} bg="#F6F4F0"
                              label="Success rate" value={successRate != null ? `${successRate}%` : "—"} />
                        <Stat testId="photo-health-stuck-now" icon={Smartphone} iconColor={stuck.count > 0 ? "#B33A2E" : "#6F6A64"}
                              bg={stuck.count > 0 ? "#FBE8E5" : "#F6F4F0"}
                              label="Stuck now" value={stuck.count}
                              sub={range === "lifetime" ? "waiting on a device right now (live)" : "still stuck now, active this period — see Lifetime for full detail"}
                              clickable={stuck.count > 0} onClick={() => setShowStuck((s) => !s)} />
                    </div>

                    {stuck.count > 0 && showStuck && (
                        <div data-testid="photo-health-stuck-list" className="mt-4 pt-4 border-t" style={{ borderColor: "#F0EDE8" }}>
                            <div className="text-[10.5px] font-bold uppercase tracking-wide mb-2" style={{ color: "#6F6A64" }}>
                                {stuck.devices} device{stuck.devices !== 1 ? "s" : ""} with photos still queued
                            </div>
                            <ul className="space-y-1.5">
                                {(stuck.items || []).map((it, i) => (
                                    <li key={i} data-testid={`photo-health-stuck-item-${i}`}
                                        onClick={() => navigate(`/app/inspections/${it.instance_id}`)}
                                        className="flex items-center justify-between gap-2 px-3 py-2 rounded-[8px] border cursor-pointer hover:bg-[#FCFBF9] transition-colors"
                                        style={{ borderColor: "#F0EDE8" }}>
                                        <div className="min-w-0">
                                            <div className="text-[12.5px] font-semibold text-[#1F1F1F] truncate">
                                                {it.user_name} <span className="font-normal" style={{ color: "#9A968F" }}>· {it.device_label}</span>
                                            </div>
                                            <div className="text-[10.5px] truncate" style={{ color: "#9A968F" }}>
                                                {it.template_name}{it.client_name ? ` · ${it.client_name}` : ""}{it.oldest_queued_at ? ` · queued ${timeAgo(it.oldest_queued_at)}` : ""}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            <span className="text-[12px] font-bold" style={{ color: "#B33A2E" }}>{it.count} photo{it.count !== 1 ? "s" : ""}</span>
                                            <ArrowRight className="w-3 h-3" style={{ color: "#9A968F" }} />
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {failed > 0 && showFailed && (
                        <div data-testid="photo-health-failed-list" className="mt-4 pt-4 border-t" style={{ borderColor: "#F0EDE8" }}>
                            <div className="text-[10.5px] font-bold uppercase tracking-wide mb-1" style={{ color: "#6F6A64" }}>
                                {failedItems.length} permanently-failed photo{failedItems.length !== 1 ? "s" : ""}
                            </div>
                            <div className="text-[10.5px] mb-2" style={{ color: "#9A968F" }}>
                                No file ever reached the server for these — the photo (if not yet cleared) is still only on the uploader's own device.
                                Open the report to ask them to retry/retake it, or dismiss once it's resolved/no longer needed.
                            </div>
                            <ul className="space-y-1.5">
                                {failedItems.map((it) => (
                                    <li key={it.event_id} data-testid={`photo-health-failed-item-${it.event_id}`}
                                        className="flex items-center justify-between gap-2 px-3 py-2 rounded-[8px] border"
                                        style={{ borderColor: "#F0EDE8" }}>
                                        <div className="min-w-0 cursor-pointer" onClick={() => !it.instance_missing && navigate(`/app/inspections/${it.instance_id}`)}>
                                            <div className="text-[12.5px] font-semibold text-[#1F1F1F] truncate">
                                                {it.title || it.template_name || "Report"}{it.client_name ? ` · ${it.client_name}` : ""}
                                            </div>
                                            <div className="text-[10.5px] truncate" style={{ color: "#9A968F" }}>
                                                {it.actor_email || "Unknown user"}{it.filename ? ` · ${it.filename}` : ""}{it.created_at ? ` · ${timeAgo(it.created_at)}` : ""}
                                                {it.error ? ` · ${it.error}` : ""}{it.instance_missing ? " · report deleted" : ""}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            {!it.instance_missing && (
                                                <ArrowRight className="w-3 h-3 cursor-pointer" style={{ color: "#9A968F" }}
                                                    onClick={() => navigate(`/app/inspections/${it.instance_id}`)} />
                                            )}
                                            <button data-testid={`photo-health-failed-dismiss-${it.event_id}`}
                                                disabled={dismissing === it.event_id}
                                                onClick={() => dismissFailed(it.event_id)}
                                                className="flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full border transition-colors hover:bg-[#FCFBF9] disabled:opacity-50"
                                                style={{ borderColor: "#E5E0DA", color: "#6F6A64" }}>
                                                <X className="w-3 h-3" /> Dismiss
                                            </button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function Stat({ testId, icon: Icon, iconColor, bg, label, value, sub, clickable, onClick }) {
    return (
        <div data-testid={testId} onClick={clickable ? onClick : undefined}
            className={`rounded-[9px] p-3.5 ${clickable ? "cursor-pointer hover:brightness-95 transition-[filter]" : ""}`}
            style={{ background: bg }}>
            <div className="flex items-center gap-1.5 mb-1.5">
                <Icon className="w-3.5 h-3.5" style={{ color: iconColor }} />
                <span className="text-[10.5px] font-bold uppercase tracking-wide" style={{ color: "#6F6A64" }}>{label}</span>
            </div>
            <div className="text-[22px] font-bold leading-none text-[#1F1F1F]">{value}</div>
            {sub && <div className="text-[10px] mt-1 leading-snug" style={{ color: "#9A968F" }}>{sub}</div>}
        </div>
    );
}
